import { useState, useEffect, useCallback, useRef } from 'react';
import { warehouseApi as api, apiError } from './warehouseApi';

export const defaultPackageFilters = () => ({ status: '', delivery_status: '', user_id: '', provider_id: '', product_id: '', item_quantity: '', product_stock: '', driver_ids: [], district: '',
  date_from: '', date_to: '', delivery_date: new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Lima', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()), page: 1, per_page: 25 });

export function usePackageDashboard(view = 'overview') {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [packages, setPackages] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesError, setPackagesError] = useState(null);
  const [filters, setFilters] = useState(defaultPackageFilters);
  const [inventory, setInventory] = useState([]);
  const [inventoryPagination, setInventoryPagination] = useState(null);
  const [inventoryFilters, setInventoryFilters] = useState({ page: 1, per_page: 25, scope: 'active', q: '', low_stock_only: false, warehouse_id: '' });
  const [inventorySummary, setInventorySummary] = useState(null);
  const [inventoryAlerts, setInventoryAlerts] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState(null);
  const [metricsError, setMetricsError] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driversError, setDriversError] = useState(null);
  const [driversRequested, setDriversRequested] = useState(false);
  const [activeUsersOnly, setActiveUsersOnly] = useState(true);
  const [usersRequested, setUsersRequested] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchPagination, setSearchPagination] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [warehouseConfig, setWarehouseConfig] = useState({ enabled: false, warehouses: [] });
  const controllers = useRef({});
  const request = useCallback(async (key, url, params, onData, setLoading, setError) => {
    controllers.current[key]?.abort();
    const controller = new AbortController();
    controllers.current[key] = controller;
    setLoading?.(true); setError?.(null);
    try {
      const { data } = await api.get(url, { params, signal: controller.signal });
      if (!controller.signal.aborted) onData(data);
    } catch (err) {
      if (!controller.signal.aborted) setError?.(apiError(err));
    } finally {
      if (!controller.signal.aborted) setLoading?.(false);
    }
  }, []);
  useEffect(() => () => Object.values(controllers.current).forEach(c => c.abort()), []);
  const fetchStats = useCallback(() => request('stats', '/api/admin/packages/stats', {}, setStats, setStatsLoading, setStatsError), [request]);
  const fetchMetrics = useCallback(() => request('metrics', '/api/admin/inventory/metrics', {}, data => {
    setInventorySummary(data.summary); setInventoryAlerts(data.alerts || []);
  }, null, setMetricsError), [request]);
  const fetchInventory = useCallback(() => request('inventory', '/api/admin/inventory/products', inventoryFilters, data => {
    setInventory(data.products); setInventoryPagination(data.pagination);
  }, setInventoryLoading, setInventoryError), [request, inventoryFilters]);
  const fetchWarehouseConfig = useCallback(() => request('config', '/api/admin/warehouse/config', {}, setWarehouseConfig, null, setInventoryError), [request]);
  const fetchPackages = useCallback(() => request('packages', '/api/admin/packages', {
    ...Object.fromEntries(Object.entries(filters).filter(([key, value]) => key !== 'driver_ids' && value !== '')),
    ...(filters.driver_ids?.length ? { driver_ids: filters.driver_ids.join(',') } : {}),
  }, data => {
    if (filters.product_id || filters.item_quantity || filters.product_stock) {
      const applied = data.applied_filters || {};
      if (String(applied.product_id || '') !== String(filters.product_id || '') ||
          String(applied.item_quantity || '') !== String(filters.item_quantity || '') ||
          String(applied.product_stock || '') !== String(filters.product_stock || '')) {
        setPackages([]); setPagination(null);
        setPackagesError('El servidor no confirmó los filtros de producto y stock. Actualiza el backend antes de continuar.');
        return;
      }
    }
    if (filters.driver_ids?.length) {
      const requested = [...new Set(filters.driver_ids.map(Number))].sort((a, b) => a - b);
      const applied = data.applied_filters?.driver_ids;
      if (!Array.isArray(applied) || requested.join(',') !== [...applied].sort((a, b) => a - b).join(',')) {
        setPackages([]);
        setPagination(null);
        setPackagesError('El servidor no confirmó el filtro por conductor. Reinicia o actualiza el backend antes de continuar.');
        return;
      }
    }
    setPackages(data.packages); setPagination(data.pagination);
  }, setPackagesLoading, message => {
    setPackagesError(message);
    if (message) { setPackages([]); setPagination(null); }
  }), [request, filters]);
  const fetchUsers = useCallback(() => request('users', '/api/admin/users/with-packages', { active_only: activeUsersOnly, q: userSearch }, data => setUsers(data.items || []), setUsersLoading, setPackagesError), [request, activeUsersOnly, userSearch]);
  const fetchDrivers = useCallback(() => request('drivers', '/api/admin/drivers', {}, data => setDrivers(data.drivers || []), setDriversLoading, setDriversError), [request]);
  const searchPackages = useCallback((q, page = 1) => {
    if (q.trim().length < 2) { controllers.current.search?.abort(); setSearchResults([]); setSearchLoading(false); return; }
    setSearchQuery(q);
    return request('search', '/api/admin/packages/search', { q, page }, data => {
      setSearchResults(data.packages || []); setSearchPagination(data.pagination);
    }, setSearchLoading, setPackagesError);
  }, [request]);
  const fetchPackageHistory = useCallback((id, page = 1) => {
    setHistory(null);
    return request('history', `/api/admin/packages/${id}/history`, { page, per_page: 25 }, setHistory, setHistoryLoading, setPackagesError);
  }, [request]);
  const applyFilter = useCallback(next => setFilters(prev => ({ ...prev, ...next, page: 1 })), []);
  const clearFilters = useCallback(() => setFilters({ ...defaultPackageFilters(), delivery_date: '' }), []);
  const goToPage = useCallback(page => setFilters(prev => ({ ...prev, page })), []);
  const applyInventoryFilter = useCallback(next => setInventoryFilters(prev => ({ ...prev, ...next, page: 1 })), []);
  const goToInventoryPage = useCallback(page => setInventoryFilters(prev => ({ ...prev, page })), []);
  const bulkUpdateStatus = useCallback(async (packageIds, newStatus) => {
    setBulkLoading(true);
    try {
      const { data } = await api.patch('/api/admin/packages/bulk-status', { package_ids: packageIds, new_status: newStatus });
      setBulkResult(data); await fetchPackages(); return data;
    } finally { setBulkLoading(false); }
  }, [fetchPackages]);

  useEffect(() => { if (view === 'overview') { fetchStats(); fetchMetrics(); } }, [view, fetchStats, fetchMetrics]);
  useEffect(() => { if (view === 'packages') fetchPackages(); }, [view, fetchPackages]);
  useEffect(() => { if (view === 'inventory') fetchInventory(); }, [view, fetchInventory]);
  useEffect(() => { if (view === 'inventory') fetchMetrics(); }, [view, fetchMetrics]);
  useEffect(() => { if (view === 'inventory' || view === 'packages') fetchWarehouseConfig(); }, [view, fetchWarehouseConfig]);
  useEffect(() => { if (view === 'packages' && usersRequested) fetchUsers(); }, [view, usersRequested, fetchUsers]);
  useEffect(() => { if (view === 'packages' && driversRequested) fetchDrivers(); }, [view, driversRequested, fetchDrivers]);
  return { stats, statsLoading, statsError, fetchStats, packages, pagination, packagesLoading, packagesError,
    filters, applyFilter, clearFilters, goToPage, fetchPackages, searchResults, searchPagination, searchLoading,
    searchQuery, setSearchQuery, searchPackages, history, historyLoading, fetchPackageHistory,
    inventory, inventorySummary, inventoryAlerts, inventoryLoading, inventoryError, metricsError, fetchInventory,
    inventoryFilters, inventoryPagination, applyInventoryFilter, goToInventoryPage, fetchMetrics, warehouseConfig, fetchWarehouseConfig,
    bulkLoading, bulkResult, bulkUpdateStatus, users, usersLoading, activeUsersOnly, setActiveUsersOnly, fetchUsers,
    drivers, driversLoading, driversError, requestDrivers: () => setDriversRequested(true),
    requestUsers: () => setUsersRequested(true), setUserSearch };
}
